import React, { useEffect, useMemo, useRef, useState } from "react";
import { auth, db } from '../../firebase';
import { onValue, ref, get, update, set } from "firebase/database";

import B2bNavBar from '../../components/b2bnavbar/B2BNav';
import './b2bDashboard.css';
import defaultTimsImage from '../../assets/logo_tims.png';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import AnimatedCheck from "../../animations/AnimatedCheck";
import Header from "../../components/header/Header";
import LoadingAnimation from "../../animations/Loading";

const B2BDashboard = () => {
    //set loading while fetching data from firebase
    const [loading, setLoading] = useState(true);
    const [userId, setUserId] = useState(null); // Track the authenticated user

    const [expandedOrderProducts, setExpandedOrderProducts] = useState({}); //since multiple orders need to be tracked for animation
    const [hideAnimation, setHideAnimation] = useState('hide');

    const [orders, setOrders] = useState({});
    const [stocks, setStocks] = useState({});
    const [products, setProducts] = useState({});

    const [orderSearch, setOrderSearch] = useState('');
    const [stockSearch, setStockSearch] = useState('');
    const [productSearch, setProductSearch] = useState('');

    const fetchData = async (userid) => {
        setLoading(true);
        try {
            //fetch orders specific to logged-in user
            const ordersRef = ref(db, 'OrdersB2B/' + userid);
            //const ordersRef = ref(db, 'Orders/');
            // Fetch stocks specific to the logged-in user
            const stocksRef = ref(db, 'Stocks/' + userid);
            const productsRef = ref(db, 'Products/');

            //const ordersSnapshot = await get(ordersRef);
            //const stocksSnapshot = await get(stocksRef);
            //const productsSnapshot = await get(productsRef);

            const [ordersSnapshot, stocksSnapshot, productsSnapshot] = await Promise.all([
                get(ordersRef),
                get(stocksRef),
                get(productsRef)
            ]);

            //save orders in reverse since latest are at bottom originally
            const ordersData = ordersSnapshot.val() || {};
            const reverseOrders = Object.entries(ordersData).reverse();//reverse order
            //convert back to object
            const reversedOrdersObject = Object.fromEntries(reverseOrders);
            setOrders(reversedOrdersObject);

            setStocks(stocksSnapshot.val() || {});
            setProducts(productsSnapshot.val() || {});
        } catch (error) {
            console.error('b2bdashboard fetchdata error: ', error);
        } finally {
            setTimeout(() => {
                setLoading(false); // Ensure UI re-renders
            }, 500); // Slight delay to allow proper re-rendering
        }
    };

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUserId(user.uid); //set the authenticated user
                fetchData(user.uid); //fetch the data for the logged in user
            } else {
                setUserId(null); //no user is logged in
            }
        });

        return () => unsubscribe(); //cleanup subscription
    }, []);

    // Filter orders based on orderSearch, only for the current user
    const filteredOrders = useMemo(() => {
        if (!orderSearch) return orders;
        return Object.fromEntries(
            Object.entries(orders).filter(([key, value]) => {
                return (
                    value.order_name?.toLowerCase().includes(orderSearch.toLowerCase()) ||
                    value.order_status?.toLowerCase().includes(orderSearch.toLowerCase())
                );
            })
        );
    }, [orderSearch, orders]);

    //filter stocks based on stockSearch, only for the current user
    const filteredStocks = useMemo(() => {
        if (!stockSearch) return stocks;
        return Object.fromEntries(
            Object.entries(stocks).filter(([key, value]) => {
                return (
                    value.stock_name?.toLowerCase().includes(stockSearch.toLowerCase()) ||
                    key.toLowerCase().includes(stockSearch.toLowerCase())
                );
            })
        );
    }, [stockSearch, stocks]);

    //filter products based on productSearch in search text
    const filteredProducts = useMemo(() => {
        if (!productSearch) return products;
        return Object.fromEntries(
            Object.entries(products).filter(([key, value]) => {
                return (
                    value.mpn?.toLowerCase().includes(productSearch.toLowerCase()) ||
                    value.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
                    value.supplier?.toLowerCase().includes(productSearch.toLowerCase()) ||
                    value.type?.toLowerCase().includes(productSearch.toLowerCase())
                );
            })
        );
    }, [productSearch, products]);

    const scrollContainerRef = useRef(null);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;

        const handleWheel = (e) => {
            e.preventDefault();
            scrollContainer.scrollLeft += e.deltaY * 2; // Horizontal scroll
        };

        scrollContainer.addEventListener("wheel", handleWheel, { passive: false });

        return () => {
            scrollContainer.removeEventListener("wheel", handleWheel);
        };
    }, []);

    // show or hide ordered products
    const showOrderedProducts = (orderId) => {
        setExpandedOrderProducts((prev) => ({ 
            ...prev, 
            [orderId]: true 
        })); // Step 1: Mount the component
        setTimeout(() => {
            setHideAnimation((prev) => ({ ...prev, [orderId]: "show" })); // Step 2: Trigger 'show' animation
        }, 10); // Tiny delay to let React mount first
    };

    const hideOrderedProducts = (orderId) => {
        setHideAnimation((prev) => ({ ...prev, [orderId]: "hide" })); // Step 1: Trigger 'hide' animation
        setTimeout(() => {
            setExpandedOrderProducts((prev) => ({ ...prev, [orderId]: false })); // Step 2: Unmount after animation
        }, 600); // Wait for animation duration before unmounting
    };

    const updateFirbaseComplete = (orderId) => {
        if (!userId) return;
        const orderRef = ref(db, `OrdersB2B/${userId}/${orderId}/order_status`);

        set(orderRef, "Complete")
            .then(() => {
                console.log('updateFirbaseComplete orders b2b status set to complete');

                //update orders list as well
                setOrders((prevOrders) => ({
                    ...prevOrders,
                    [orderId]: {
                        ...prevOrders[orderId],
                        order_status: "Complete"
                    }
                }));
            })
            .catch((error) => {
                console.log('updateFirbaseComplete orders b2b error updating: ', error);
            });
    }

    return (
        <div className="b2b-div-main-container">
            {/* top header section*/}
            <Header title="Dashboard" />

            {/* body section */}
            <div className="b2b-div-body">
                {/* Left Navigation Bar for b2b */}
                <B2bNavBar />

                {/* main content section */}
                <div className="b2b-div-content">
                    {/* Orders and Stock Section */}
                    <div className="b2b-div-content-top-section">
                        {/* b2b orders section with search bar 70% width*/}
                        <div className="b2b-div-orders-section">
                            <span>
                                <h4>Orders</h4>

                                <input
                                    type="text"
                                    placeholder="Search Orders by name or status"
                                    value={orderSearch}
                                    onChange={(e) => setOrderSearch(e.target.value)}
                                />
                            </span>

                            {loading ? (
                                <LoadingAnimation />
                            ) : (
                                <div className="b2b-div-orders-list" id="scroll-style">
                                    {/* Orders - Grid Layout */}
                                    {Object.entries(filteredOrders).map(([orderId, order]) => {
                                        //precompute the total cost
                                        let totalcost = 0;
                                        let lastUpatedAt = new Date(order.last_updated_at).toLocaleString();
                                        let addDate = new Date(order.added_date).toLocaleString();

                                        const productList = order.products.map((product, index) => {
                                            const productData = products[product.mpn] || {}; // initialise object at least if empty
                                            const wholesaleCost = productData.wholesale || 0; //initialise to 0 to not available
                                            totalcost += product.quantity * wholesaleCost;

                                            return (
                                                <li key={index}>
                                                    {product.name} : {product.quantity} (${products[product.mpn]?.wholesale || 0})
                                                </li>
                                            );
                                        });

                                        return (
                                            <div key={orderId} className="b2b-div-order-card">
                                                <div className="b2b-div-order-details">
                                                    <span className="b2b-div-order-id">#{orderId}</span>
                                                    <span className="b2b-div-order-date">{addDate}</span>
                                                    <h6>{order.order_name}</h6>
                                                    <span className="b2b-div-order-description">{order.order_description}</span>
                                                </div>

                                                <div className="b2b-div-order-cost">
                                                    Total Cost:
                                                    <span> ${totalcost.toFixed(2)}</span>
                                                </div>

                                                <div className="b2b-div-order-status">
                                                    {order.order_status === "Delivered" && (
                                                        <div className="b2b-div-order-accept" title="Acknowledge Delivery" onClick={() => updateFirbaseComplete(orderId)}>
                                                            <span>Accept</span>
                                                            <AnimatedCheck />
                                                        </div>
                                                    )}
                                                    <span>{lastUpatedAt}</span>
                                                    <div className={`b2b-div-order-status-img ${order.order_status}`}>
                                                        {/* Circles */}
                                                        <div className="b2b-div-status-circle"></div>
                                                        <div className="b2b-div-status-circle"></div>
                                                        <div className="b2b-div-status-circle"></div>
                                                        <div className="b2b-div-status-circle"></div>

                                                        {/* Connecting Line */}
                                                        <div className="b2b-div-status-line"></div>
                                                    </div>
                                                    <p className={`${order.order_status}`}>{order.order_status}</p>
                                                </div>

                                                <div className="b2b-div-order-products">
                                                    <span onClick={() => showOrderedProducts(orderId)} title="Click for details">Products <HelpOutlineIcon className="infoicon" />
                                                    </span>
                                                    {expandedOrderProducts[orderId] && (
                                                        <div className={`order-products-container ${hideAnimation[orderId] || ""}`}>
                                                            <CancelIcon className="cancelIcon" onClick={() => hideOrderedProducts(orderId)}></CancelIcon>
                                                            <div className="white-bg-icon"></div>
                                                            <ul id="scroll-style">
                                                                {productList}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* b2b stock section with search bar 30% width*/}
                        <div className="b2b-div-stocks-section">
                            <span className="b2b-span-stocks-header">
                                <h4>Stocks</h4>
                                <input
                                    type="text"
                                    placeholder="Search Stocks by name or mpn"
                                    value={stockSearch}
                                    onChange={(e) => setStockSearch(e.target.value)}
                                />
                            </span>

                            {loading ? (
                                <LoadingAnimation />
                            ) : (
                                <div className="b2b-div-stocks-list">
                                    {/* stocks - table layout */}
                                    <table className="b2b-table-stocks">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th className="b2b-col-qty">Qty</th>
                                                <th className="b2b-col-cost">Cost</th>
                                                <th>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody className="b2b-table-stocks-body" id="scroll-style">
                                            {Object.entries(filteredStocks).map(([productId, stock]) => {
                                                const product = products[productId];
                                                //const totalCost = stock.stock_quantity * stock.stock_cost; //calc total cost each
                                                const totalCost = stock.stock_quantity * product?.wholesale; //calc total cost each
                                                return (
                                                    <tr key={productId}>
                                                        <td>{stock.stock_name}</td>
                                                        <td className="b2b-col-qty">{stock.stock_quantity}</td>
                                                        {/* <td className="b2b-col-cost">${stock.stock_cost}</td> */}
                                                        <td className="b2b-col-cost">${product?.wholesale}</td>
                                                        <td>${totalCost.toFixed(2)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="2"><strong>Total Cost</strong></td>
                                                <td>
                                                    <strong>
                                                        ${Object.entries(filteredStocks).reduce((totalSum, [productId, stock]) => {
                                                            const product = products[productId];
                                                            return totalSum + stock.stock_quantity * product?.wholesale;
                                                            //return totalSum + stock.stock_quantity * stock.stock_cost;
                                                        }, 0).toFixed(2)}
                                                    </strong>
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Products only Section */}
                    <div className="b2b-div-content-bottom-section">
                        <div className="b2b-div-products-section">
                            <span>
                                <h4>Products</h4>
                                <input
                                    type="text"
                                    placeholder="Search Products by name, mpn, supplier or type"
                                    value={productSearch}
                                    onChange={(e) => setProductSearch(e.target.value)}
                                />
                            </span>

                            {loading ? (
                                <LoadingAnimation />
                            ) : (
                                <div className="b2b-div-products-list" ref={scrollContainerRef} id="scroll-style">
                                    {
                                        Object.entries(filteredProducts).map(([productId, product]) => (
                                            <div key={productId} className="b2b-div-product-card">
                                                <img src={product.imageUrl || defaultTimsImage} alt={product.mpn} />
                                                <h6>{product.name}</h6>
                                                <div className="price-badge">
                                                    <p>${product.wholesale || 0}</p>
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>
                            )}
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default B2BDashboard;

// {isShowCreateOrder && (
//     <div className={`wo-div-create-orders ${animationCreateOrder}`}>
//         <div className="wo-div-co-header">
//             <div className="wo-div-co-header-top">
//                 <span>Orders form</span>
//                 <ClearIcon className="cancelIcon" onClick={() => hideCreateOrderForm()}></ClearIcon>
//             </div>
//             <div className="wo-div-co-header-base">
//                 <input type="text"
//                     placeholder="Search products by name, mpn, supplier or type"
//                     value={productSearchQuery}
//                     onChange={(e) => setProductSearchQuery(e.target.value)}
//                 />

//                 <select value={selectOrder} onChange={(e) => handleOrderSelect(e.target.value)}>
//                     <option value="" disabled>Classic</option>
//                     <option value="">Steer and drive</option>
//                     <option value="">Steer and dual drive</option>
//                     <option value="">Dual steer and dual drive</option>
//                     <option value="">Steer, drive and trailer-a</option>
//                     <option value="">Dual steer, drive and trailer-a</option>
//                     <option value="">Steer, dual drive and trailer-a</option>
//                     <option value="">Steer and drive, and dual trailer-a</option>
//                     <option value="">Steer, drive, trailer-a and trailer-b</option>
//                 </select>
//                 <select value={selectOrder} onChange={(e) => handleOrderSelect(e.target.value)}>
//                     <option value="" disabled>Compact</option>
//                     <option value="">Steer and drive</option>
//                     <option value="">Steer and dual drive</option>
//                     <option value="">Dual steer and dual drive</option>
//                     <option value="">Steer, drive and trailer-a</option>
//                     <option value="">Dual steer, drive and trailer-a</option>
//                     <option value="">Steer, dual drive and trailer-a</option>
//                     <option value="">Steer and drive, and dual trailer-a</option>
//                     <option value="">Steer, drive, trailer-a and trailer-b</option>
//                 </select>
//                 <select value={selectOrder} onChange={(e) => handleOrderSelect(e.target.value)}>
//                     <option value="" disabled>Advanced</option>
//                     <option value="">Steer and drive</option>
//                     <option value="">Steer and dual drive</option>
//                     <option value="">Dual steer and dual drive</option>
//                     <option value="">Steer, drive and trailer-a</option>
//                     <option value="">Dual steer, drive and trailer-a</option>
//                     <option value="">Steer, dual drive and trailer-a</option>
//                     <option value="">Steer and drive, and dual trailer-a</option>
//                     <option value="">Steer, drive, trailer-a and trailer-b</option>
//                 </select>
//                 <select value={selectOrder} onChange={(e) => handleOrderSelect(e.target.value)}>
//                     <option value="" disabled>Previous orders</option>
//                     {Object.entries(orders).map(([orderId, order]) => (
//                         <option key={orderId} value={orderId}>
//                             {order.order_name} {orderId}
//                         </option>
//                     ))}
//                 </select>
//             </div>
//         </div>

//         <div className="wo-div-co-body">
//             {loading ? (
//                 <LoadingAnimation />
//             ) : (
//                 <div className="wo-div-co-body-products-list">
//                     {/* stocks - table layout */}
//                     <table className="wo-table-products">
//                         <thead>
//                             <tr>
//                                 <th>Image</th>
//                                 <th className="wo-col-name">Name</th>
//                                 <th className="wo-col-qty">Qty</th>
//                                 <th className="wo-col-cost">Wholesale</th>
//                                 <th className="wo-col-action">Action</th>
//                             </tr>
//                         </thead>
//                         <tbody className="wo-table-products-body" id="scroll-style">
//                             {Object.entries(filteredProducts).map(([productId, product]) => (
//                                 <tr key={productId}>
//                                     <td>
//                                         <img src={product.imageUrl || defaultTimsImage} alt={product.mpn} style={{ width: '100px', height: '100px' }} />
//                                     </td>
//                                     <td className="wo-col-name">{product.name}</td>
//                                     <td className="wo-col-qty">
//                                         <input
//                                             min="0"
//                                             type="number"
//                                             placeholder="0"
//                                             value={opQuanity[productId] ?? ""}
//                                             onChange={(e) => {
//                                                 const newQuantity = Math.max(0, parseInt(e.target.value, 10) || 0);
//                                                 handleOPQuantityChange(productId, newQuantity);
//                                             }} />
//                                     </td>
//                                     <td className="wo-col-cost">${product.wholesale || 0}</td>
//                                     <td className="wo-col-action"><button onClick={() => handleAddToCart(productId, product)}>Add To Cart</button></td>
//                                 </tr>
//                             ))
//                             }
//                         </tbody>
//                     </table>
//                 </div>
//             )}
//             <div className="wo-div-co-body-cart">
//                 {/* Selected Products List */}
//                 <div className="wo-div-cart-header"><h3>Cart</h3><ShoppingCartIcon /></div>

//                 <table className="b2b-table-stocks">
//                     <thead>
//                         <tr>
//                             <th>Item</th>
//                             <th className="b2b-col-qty">Qty</th>
//                             <th className="b2b-col-cost">Price</th>
//                             <th>Action</th>
//                         </tr>
//                     </thead>
//                     <tbody className="b2b-table-stocks-body" id="scroll-style">
//                     {orderingProductList.reverse().map((product, index) => (
                            
//                             <tr key={index}>
//                                     <td>{product.name}</td>
//                                     <td className="b2b-col-qty">{product.quantity}- pcs</td>
//                                     <td className="b2b-col-cost">$ {product.price}</td>
//                                     <td>Delete</td>
//                                 </tr>
//                         ))}
//                     </tbody>
//                 </table>



//                 {/* <ul>
//                     {orderingProductList.reverse().map((product, index) => (
//                         <li key={index}>
//                             <span>{product.name}</span>
//                             <span>{product.quantity}- pcs</span>
//                             <span>$ {product.price}</span>
//                         </li>
//                     ))}
//                 </ul> */}
//             </div>
//         </div>

//         <div className="wo-div-co-checkout-details">
//         </div>
//     </div>
// )}